// Cache identities for the `./letters` door.
//
// THE LITERALS ARE FROZEN AND ARE NOT NEW. Each is transcribed from the app hook
// that declared it, and `queryKeys.freeze.test.ts` pins the whole TUPLE rather
// than the head, because a key is a cache identity: change a literal and every
// in-flight entry is orphaned while every prefix invalidator silently stops
// matching.
//
// WHY THE DECLARATIONS HAD TO MOVE AT ALL. A package `/data` hook may not import
// `@/hooks/query/**` or `@/hooks/mutation/**`, so each identity has to
// single-site here for its hook to carve.
//
// The two arrived by different routes, and the difference is visible at the old
// paths. `src/hooks/query/integrations/useQueryEhrLetterTemplates.ts` re-exports
// `GET_EHR_LETTER_TEMPLATES` name-intact, so everything that already imported it
// from there resolves unchanged. `SEND_EHR_LETTER` was MODULE-PRIVATE in the app
// hook — declared, read by the mutation and by its `useMutationState` sibling,
// exported to nobody — and it is exported here because a `/contracts` key
// factory is the sanctioned single declaration site. The old path imports it
// rather than re-declaring it, and does NOT re-export it: nothing has ever
// imported that name, so publishing it would widen the app surface for no
// consumer.
//
// NOTE THE SPELLING, WHICH IS NOT THIS DOOR'S TO NORMALISE: the read's value is
// `GetEHRLetterTemplates` — `EHR` in caps where every other head on the
// neighbouring `./ehr` door writes `Ehr`. Normalising it would be a cache flush.
import type { EHRProvider } from '@heidi/schema/ml-scribe';

export const CREATE_LETTER_FROM_DOCUMENT = 'CreateLetterFromDocument';
export const DELETE_LETTER = 'DeleteLetter';
export const DELETE_LETTER_ATTACHMENT_V2 = 'deleteLetterAttachmentV2';
export const SEND_LETTER = 'SendLetter';
export const SIGN_LETTER = 'SignLetter';
export const UPDATE_LETTER = 'UpdateLetter';
export const UPLOAD_LETTER_ATTACHMENTS_V2 = 'uploadLetterAttachmentsV2';

export const GET_LETTER_ATTACHMENT_DOWNLOAD_URL_V2 =
  '/api/v2/ml-scribe/sessions/{session_id}/letters/{letter_id}/attachments/{attachment_id}/download-url';

export const getLetterAttachmentDownloadUrlV2QueryKey = ({
  sessionId,
  letterId,
  attachmentId,
}: {
  sessionId: string;
  letterId: string;
  attachmentId: string;
}) =>
  [GET_LETTER_ATTACHMENT_DOWNLOAD_URL_V2, { sessionId, letterId, attachmentId }] as const;

export const GET_LETTER_ATTACHMENTS_V2 =
  '/api/v2/ml-scribe/sessions/{session_id}/letters/{letter_id}/attachments';

export const getLetterAttachmentsV2QueryKey = ({
  sessionId,
  letterId,
}: {
  sessionId: string;
  letterId?: string;
}) => [GET_LETTER_ATTACHMENTS_V2, { sessionId, letterId }] as const;

export const GET_LETTER_HEAD =
  '/api/v2/ml-scribe/sessions/{session_id}/letters/{letter_id}/letterhead';

export const GET_LETTER_TIMELINE_EVENTS =
  '/api/v2/ml-scribe/sessions/{session_id}/letters/{letter_id}/timeline-events';

export const getLetterTimelineEventsQueryKey = ({
  sessionId,
  letterId,
}: {
  sessionId: string;
  letterId?: string;
}) => [GET_LETTER_TIMELINE_EVENTS, { sessionId, letterId }] as const;

export const GET_LETTERS = 'letters';
export const LETTER_SENDER_PROVIDERS_QUERY_KEY = 'letterSender_providers';
export const LETTER_SENDER_NOTE_PANEL_DEBUGGER_STORAGE_KEY =
  'letter-sender-v2-note-panel-debugger-view';

/** `GET /ehr/letter/templates`. */
export const GET_EHR_LETTER_TEMPLATES = 'GetEHRLetterTemplates';

/** `POST /ehr/letter/session/{sessionId}/outgoing-correspondence`. */
export const SEND_EHR_LETTER = 'SendEhrLetter';

/** TanStack query keys for the `./letters` door's carved read hooks. */
export const lettersQueryKeys = {
  /**
   * A BARE provider in slot 2, NOT `{ integrationEhrProvider }`. Transcribed
   * from the app hook, whose neighbours on `./ehr` wrap their single argument;
   * wrapping it here would move every cached template list to a new identity and
   * break any `invalidateQueries` written against the current shape.
   *
   * The slot is kept when the provider is undefined. The app hook builds the key
   * unconditionally and gates the fetch with `enabled`, so `[head, undefined]`
   * is a real cached entry rather than a degenerate one.
   */
  ehrLetterTemplates: (integrationEhrProvider: EHRProvider | undefined) =>
    [GET_EHR_LETTER_TEMPLATES, integrationEhrProvider] as const,
};

/** TanStack mutation keys for the `./letters` door's carved write hooks. */
export const lettersMutationKeys = {
  /**
   * A WRAPPED `sessionId` in slot 2, and the wrapper is load-bearing rather than
   * stylistic: `useMutateStateSendEhrLetter` filters `useMutationState` on the
   * same tuple, so flattening it here would stop that hook reporting `isPending`
   * with nothing failing.
   */
  sendEhrLetter: (sessionId: string) => [SEND_EHR_LETTER, { sessionId }] as const,
};
